import React, { useState, useEffect, useRef, Suspense, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Icons from '@fortawesome/free-solid-svg-icons';
import "../styles/Dashboard.css";

// ==============================================
// IMPORT MODULE CONFIGURATIONS
// ==============================================
import { getModuleByRole } from '../module_configs';

// ==============================================
// INSTANT TRANSLATOR CLASS
// ==============================================
class InstantTranslator {
  constructor() {
    this.cache = new Map();
    this.loadFromCache();
  }

  loadFromCache() {
    try {
      const cached = localStorage.getItem('ourzest_translations');
      if (cached) {
        const parsed = JSON.parse(cached);
        Object.entries(parsed).forEach(([key, value]) => this.cache.set(key, value));
      }
    } catch (e) {}
  }

  saveToCache() {
    try {
      const cacheObj = {};
      this.cache.forEach((value, key) => { cacheObj[key] = value; });
      localStorage.setItem('ourzest_translations', JSON.stringify(cacheObj));
    } catch (e) {}
  }

  defaultTexts = {
    dashboard: 'Dashboard',
    welcome: 'Welcome back',
    searchPlaceholder: 'Search menus, submenus...',
    notifications: 'Notifications',
    markAllRead: 'Mark all as read',
    viewAll: 'View All',
    myProfile: 'My Profile',
    settings: 'Settings',
    helpSupport: 'Help & Support',
    logout: 'Logout',
    modules: 'MODULES',
    systemOnline: 'System Online',
    switchShops: 'SWITCH SHOP',
    availableShops: 'Available Shops',
    loadingShops: 'Loading shops...',
    noShopsAvailable: 'No shops available',
    refreshShops: 'Refresh Shops',
    role: 'Role',
    close: 'Close',
    loading: 'Loading workspace...',
    themeColor: 'THEME COLOR'
  };

  async translateText(text, targetLang) {
    if (targetLang === 'en') return text;
    const cacheKey = `${text}_${targetLang}`;
    if (this.cache.has(cacheKey)) return this.cache.get(cacheKey);
    
    try {
      const response = await fetch(`https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`);
      if (response.ok) {
        const data = await response.json();
        const translated = data?.responseData?.translatedText || text;
        this.cache.set(cacheKey, translated);
        this.saveToCache();
        return translated;
      }
    } catch (e) {}
    return text;
  }

  async getTranslations(lang) {
    if (lang === 'en') return this.defaultTexts;
    const translations = { ...this.defaultTexts };
    const promises = Object.entries(this.defaultTexts).map(async ([key, text]) => {
      translations[key] = await this.translateText(text, lang);
    });
    await Promise.all(promises);
    return translations;
  }
}

const translator = new InstantTranslator();

const LANGUAGES = {
  en: { name: 'English', icon: '🇺🇸', dir: 'ltr' },
  ur: { name: 'اردو', icon: '🇵🇰', dir: 'rtl' },
  ar: { name: 'عربي', icon: '🇸🇦', dir: 'rtl' },
  es: { name: 'Español', icon: '🇪🇸', dir: 'ltr' }
};

// 16 VIBRANT PROFESSIONAL THEME PRESETS
const THEME_PRESETS = [
  { name: 'Indigo', hex: '#6366f1' },
  { name: 'Cyan', hex: '#00BCD4' },
  { name: 'Emerald', hex: '#10b981' },
  { name: 'Purple', hex: '#8b5cf6' },
  { name: 'Rose', hex: '#f43f5e' },
  { name: 'Amber', hex: '#f59e0b' },
  { name: 'Blue', hex: '#3b82f6' },
  { name: 'Violet', hex: '#7c3aed' },
  { name: 'Teal', hex: '#14b8a6' },
  { name: 'Crimson', hex: '#e11d48' },
  { name: 'Orange', hex: '#ea580c' },
  { name: 'Sky', hex: '#0284c7' },
  { name: 'Pink', hex: '#ec4899' },
  { name: 'Lime', hex: '#84cc16' },
  { name: 'Sapphire', hex: '#2563eb' },
  { name: 'Coral', hex: '#ff6b6b' }
];

// Helper to calculate 2-letter user initials
const getInitials = (name) => {
  if (!name) return 'OU';
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

// Icon loader helper
const getIcon = (iconName) => {
  if (!iconName) return Icons.faBars;
  if (typeof iconName === 'string') {
    const iconKey = Object.keys(Icons).find(
      key => key.toLowerCase() === iconName.toLowerCase() || key.toLowerCase() === `fa${iconName}`.toLowerCase()
    );
    return iconKey ? Icons[iconKey] : Icons.faLayerGroup;
  }
  return iconName || Icons.faBars;
};

// ==============================================
// MAIN DASHBOARD COMPONENT
// ==============================================
const DashboardBusinessManager = () => {
  const { user, logout, setUser, currentShop } = useAuth();
  const navigate = useNavigate();

  // Role & Module Config
  const moduleConfig = getModuleByRole(user?.role);
  
  // Dynamic Business Info
  const [businessData, setBusinessData] = useState({
    businessname: user?.businessname || user?.shopname || 'OurZest Enterprise',
    bussinesslogo: null,
    profile_image: null,
    fullname: user?.fullname || user?.username || 'User'
  });

  // UI & Navigation States
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  const [mobileFocusedMenu, setMobileFocusedMenu] = useState(null);

  // Active Menu, Submenu & Collapsible Sections
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [activeSubmenu, setActiveSubmenu] = useState('');
  const [expandedMenuIds, setExpandedMenuIds] = useState(['dashboard']);
  const [collapsedSectionIds, setCollapsedSectionIds] = useState([]);

  // Search States (Desktop & Mobile)
  const [desktopSearchQuery, setDesktopSearchQuery] = useState('');
  const [mobileSearchQuery, setMobileSearchQuery] = useState('');

  const [formContent, setFormContent] = useState(null);
  const [breadcrumbItems, setBreadcrumbItems] = useState([
    { id: 'dashboard', label: 'Dashboard', isClickable: true }
  ]);
  const [loading, setLoading] = useState(true);

  // Preference States
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('ourzest_darkMode') === 'true');
  const [currentLanguage, setCurrentLanguage] = useState(() => localStorage.getItem('ourzest_language') || 'en');
  const [currentTheme, setCurrentTheme] = useState(() => localStorage.getItem('ourzest_theme') || '#6366f1');
  const [translations, setTranslations] = useState({});
  const [translatedMenus, setTranslatedMenus] = useState([]);

  // Collapsed Sidebar Hover Popover State
  const [hoveredMenu, setHoveredMenu] = useState(null);
  const [flyoutPosition, setFlyoutPosition] = useState({ top: 0 });
  const hoverTimerRef = useRef(null);

  // Dropdown Visibility States
  const [showShopSwitcher, setShowShopSwitcher] = useState(false);
  const [showThemePicker, setShowThemePicker] = useState(false);
  
  // Shops List
  const [userShops, setUserShops] = useState([]);
  const [loadingShops, setLoadingShops] = useState(false);

  // Outside Click Refs
  const shopSwitcherRef = useRef(null);
  const themePickerRef = useRef(null);
  const desktopSearchRef = useRef(null);

  // ==============================================
  // LOCK MOBILE VIEWPORT & ORIENTATION (PORTRAIT LOCK)
  // ==============================================
  useEffect(() => {
    let meta = document.querySelector('meta[name="viewport"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'viewport';
      document.head.appendChild(meta);
    }
    meta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover';

    // Attempt native Screen Orientation lock to portrait if supported
    if (window.screen && window.screen.orientation && window.screen.orientation.lock) {
      window.screen.orientation.lock('portrait').catch(() => {
        // Ignored if unsupported or user gesture required
      });
    }

    const handleTouchMove = (e) => {
      if (e.touches && e.touches.length > 1) {
        e.preventDefault();
      }
    };

    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    return () => {
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  // ==============================================
  // FETCH BUSINESS INFO FROM BACKEND
  // ==============================================
  const fetchBusinessInfo = useCallback(async () => {
    try {
      const response = await fetch('https://ourzestapp.online/oz_backend/get_business_info.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'get_business_info',
          username: user?.username,
          user_id: user?.id
        })
      });
      const data = await response.json();
      if (data && (data.status === 'success' || data.businessname)) {
        setBusinessData({
          businessname: data.businessname || user?.businessname || 'OurZest App',
          bussinesslogo: data.bussinesslogo || null,
          profile_image: data.profile_image || null,
          fullname: data.fullname || user?.fullname || user?.username || 'User'
        });

        const storedTheme = localStorage.getItem('ourzest_theme') || data.themecolor || '#6366f1';
        applyTheme(storedTheme);
      } else {
        const storedTheme = localStorage.getItem('ourzest_theme') || '#6366f1';
        applyTheme(storedTheme);
      }
    } catch (err) {
      console.warn("Error fetching business info:", err);
      const storedTheme = localStorage.getItem('ourzest_theme') || '#6366f1';
      applyTheme(storedTheme);
    }
  }, [user]);

  // ==============================================
  // INITIALIZATION
  // ==============================================
  useEffect(() => {
    const initDashboard = async () => {
      setLoading(true);
      
      const savedTheme = localStorage.getItem('ourzest_theme') || '#6366f1';
      applyTheme(savedTheme);
      
      if (darkMode) {
        document.body.classList.add('dark-mode');
      } else {
        document.body.classList.remove('dark-mode');
      }

      await fetchBusinessInfo();
      const trans = await translator.getTranslations(currentLanguage);
      setTranslations(trans);

      const DefaultDash = moduleConfig?.dashboardComponent || (() => (
        <div className="default-dashboard-view">
          <div className="welcome-banner">
            <h2>{trans.welcome}, {businessData.fullname} 👋</h2>
            <p>Select a module from the navigation to begin.</p>
          </div>
        </div>
      ));

      setFormContent(<DefaultDash onNavigate={handleMenuNavigation} updateBreadcrumbs={updateBreadcrumbs} />);
      
      setBreadcrumbItems([
        { id: 'dashboard', label: trans.dashboard || 'Dashboard', isClickable: true }
      ]);

      setLoading(false);
    };

    initDashboard();
  }, [fetchBusinessInfo]);

  useEffect(() => {
    loadTranslationsAndMenus();
  }, [currentLanguage, user?.role]);

  // ==============================================
  // THEME APPLY & RIPPLE ANIMATION
  // ==============================================
  const applyTheme = (colorHex) => {
    const hexColor = /^#[0-9A-F]{6}$/i.test(colorHex) ? colorHex : '#6366f1';
    localStorage.setItem('ourzest_theme', hexColor);
    setCurrentTheme(hexColor);

    let hex = hexColor.replace('#', '');
    if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
    const r = parseInt(hex.substring(0, 2), 16) || 99;
    const g = parseInt(hex.substring(2, 4), 16) || 102;
    const b = parseInt(hex.substring(4, 6), 16) || 241;

    document.documentElement.style.setProperty('--primary-color', hexColor);
    document.documentElement.style.setProperty('--primary-rgb', `${r}, ${g}, ${b}`);
    document.documentElement.style.setProperty('--primary-shadow', `rgba(${r}, ${g}, ${b}, 0.2)`);
  };

  const toggleDarkModeWithRipple = (e) => {
    const button = e.currentTarget;
    const rect = button.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;

    document.documentElement.style.setProperty('--ripple-x', `${x}px`);
    document.documentElement.style.setProperty('--ripple-y', `${y}px`);

    const updateThemeState = () => {
      const nextDark = !darkMode;
      setDarkMode(nextDark);
      localStorage.setItem('ourzest_darkMode', nextDark.toString());
      document.body.classList.toggle('dark-mode', nextDark);
    };

    if (document.startViewTransition) {
      document.startViewTransition(() => {
        updateThemeState();
      });
    } else {
      updateThemeState();
    }
  };

  // ==============================================
  // MENU TRANSLATION & ROLE FILTERING
  // ==============================================
  const loadTranslationsAndMenus = async () => {
    if (!moduleConfig) return;
    const trans = await translator.getTranslations(currentLanguage);
    const userRole = user?.role || 'user';

    const menus = (moduleConfig.menus || []).filter(menu => 
      menu.roles.includes('all') || menu.roles.includes(userRole) || userRole === 'super_admin'
    ).map(menu => ({
      ...menu,
      translatedName: trans[menu.id] || menu.label,
      submenus: (menu.submenus || []).filter(sub =>
        sub.roles.includes('all') || sub.roles.includes(userRole) || userRole === 'super_admin'
      ).map(sub => ({
        ...sub,
        translatedName: trans[sub.id] || sub.label
      }))
    }));

    setTranslatedMenus(menus);
  };

  // Resolve Stock, Sale, Purchase for Mobile Dock
  const stockMenuItem = useMemo(() => {
    return translatedMenus.find(m => 
      m.id.toLowerCase().includes('stock') || 
      m.id.toLowerCase().includes('inventory') ||
      m.label.toLowerCase().includes('stock')
    );
  }, [translatedMenus]);

  const saleMenuItem = useMemo(() => {
    return translatedMenus.find(m => 
      m.id.toLowerCase().includes('sale') || 
      m.id.toLowerCase().includes('pos') ||
      m.label.toLowerCase().includes('sale')
    );
  }, [translatedMenus]);

  const purchaseMenuItem = useMemo(() => {
    return translatedMenus.find(m => 
      m.id.toLowerCase().includes('purchase') || 
      m.id.toLowerCase().includes('po') ||
      m.label.toLowerCase().includes('purchase')
    );
  }, [translatedMenus]);

  // Grouped Sections
  const groupedMenuSections = useMemo(() => {
    const groupsMap = new Map();
    const defaultGroups = moduleConfig?.menuGroups || moduleConfig?.menuSections || [];

    if (Array.isArray(defaultGroups)) {
      defaultGroups.forEach(g => {
        const id = g.id || g.name;
        groupsMap.set(id, {
          id: id,
          name: g.name || g.label || id,
          icon: g.icon || 'faLayerGroup',
          menus: []
        });
      });
    }

    translatedMenus.forEach(menu => {
      const groupId = menu.groupId || menu.group || menu.section || 'main';
      const groupName = menu.sectionName || menu.groupName || menu.section || menu.group || (groupId !== 'main' ? groupId : 'Main Navigation');

      if (!groupsMap.has(groupId)) {
        groupsMap.set(groupId, {
          id: groupId,
          name: groupName,
          icon: menu.groupIcon || 'faLayerGroup',
          menus: []
        });
      }
      groupsMap.get(groupId).menus.push(menu);
    });

    return Array.from(groupsMap.values()).filter(group => group.menus.length > 0);
  }, [translatedMenus, moduleConfig]);

  const toggleSectionCollapse = (sectionId) => {
    setCollapsedSectionIds(prev =>
      prev.includes(sectionId) ? prev.filter(id => id !== sectionId) : [...prev, sectionId]
    );
  };

  // ==============================================
  // SEARCH FUNCTIONALITY FOR MENUS & SUBMENUS
  // ==============================================
  const getSearchResults = useCallback((query) => {
    if (!query || !query.trim()) return [];
    const q = query.toLowerCase().trim();
    const results = [];

    translatedMenus.forEach(menu => {
      const menuName = (menu.translatedName || menu.label || '').toLowerCase();
      if (menuName.includes(q)) {
        results.push({
          type: 'menu',
          menuId: menu.id,
          submenuId: null,
          title: menu.translatedName || menu.label,
          icon: menu.icon,
          parent: 'Main Menu'
        });
      }

      if (menu.submenus) {
        menu.submenus.forEach(sub => {
          const subName = (sub.translatedName || sub.label || '').toLowerCase();
          if (subName.includes(q)) {
            results.push({
              type: 'submenu',
              menuId: menu.id,
              submenuId: sub.id,
              title: sub.translatedName || sub.label,
              icon: sub.icon || menu.icon,
              parent: menu.translatedName || menu.label
            });
          }
        });
      }
    });

    return results;
  }, [translatedMenus]);

  // ==============================================
  // MENU TOGGLE & SUBMENU HANDLERS
  // ==============================================
  const toggleMenuExpansion = (menuId) => {
    setExpandedMenuIds(prev => 
      prev.includes(menuId) ? prev.filter(id => id !== menuId) : [...prev, menuId]
    );
  };

  const handleMenuClick = (menuId) => {
    const menu = translatedMenus.find(m => m.id === menuId);
    const hasSubmenus = menu?.submenus && menu.submenus.length > 0;

    if (hasSubmenus) {
      toggleMenuExpansion(menuId);
    }

    setActiveMenu(menuId);
    setHoveredMenu(null);
    setMobileSheetOpen(false);
    setMobileFocusedMenu(null);

    if (menuId === 'dashboard') {
      const DashboardComp = moduleConfig?.dashboardComponent || (() => <div className="p-4">Dashboard View</div>);
      setFormContent(<DashboardComp onNavigate={handleMenuNavigation} updateBreadcrumbs={updateBreadcrumbs} />);
      updateBreadcrumbs('dashboard');
    } else if (menu?.component) {
      setFormContent(React.createElement(menu.component));
      updateBreadcrumbs(menuId);
    } else if (hasSubmenus) {
      if (!activeSubmenu || !menu.submenus.some(s => s.id === activeSubmenu)) {
        const firstSub = menu.submenus[0];
        setActiveSubmenu(firstSub.id);
        if (firstSub.component) {
          setFormContent(React.createElement(firstSub.component));
        }
        updateBreadcrumbs(menuId, firstSub.id);
      }
    }
  };

  const handleSubmenuClick = (menuId, submenuId) => {
    setActiveMenu(menuId);
    setActiveSubmenu(submenuId);
    setHoveredMenu(null);
    setMobileSheetOpen(false);
    setMobileFocusedMenu(null);

    if (!expandedMenuIds.includes(menuId)) {
      setExpandedMenuIds(prev => [...prev, menuId]);
    }

    const menu = translatedMenus.find(m => m.id === menuId);
    if (menu) {
      const sub = menu.submenus.find(s => s.id === submenuId);
      if (sub?.component) {
        setFormContent(React.createElement(sub.component));
      }
    }
    updateBreadcrumbs(menuId, submenuId);
  };

  const handleMenuNavigation = (menuId, submenuId = null) => {
    if (submenuId) {
      handleSubmenuClick(menuId, submenuId);
    } else {
      handleMenuClick(menuId);
    }
  };

  const updateBreadcrumbs = (menuId, submenuId = null) => {
    const crumbs = [
      { id: 'dashboard', label: translations.dashboard || 'Dashboard', isClickable: true }
    ];

    if (menuId && menuId !== 'dashboard') {
      const menu = translatedMenus.find(m => m.id === menuId);
      if (menu) {
        crumbs.push({
          id: menu.id,
          label: menu.translatedName || menu.label,
          isClickable: true
        });
      }
    }

    if (submenuId) {
      const menu = translatedMenus.find(m => m.id === menuId);
      const sub = menu?.submenus?.find(s => s.id === submenuId);
      if (sub) {
        crumbs.push({
          id: sub.id,
          label: sub.translatedName || sub.label,
          isClickable: false
        });
      }
    }

    setBreadcrumbItems(crumbs);
  };

  // ==============================================
  // COLLAPSED HOVER FLYOUT WITH BRIDGE
  // ==============================================
  const handleMouseEnterMenuItem = (e, menu) => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);

    if (sidebarCollapsed && menu.submenus && menu.submenus.length > 0) {
      const rect = e.currentTarget.getBoundingClientRect();
      setFlyoutPosition({ top: rect.top });
      setHoveredMenu(menu);
    }
  };

  const handleMouseLeaveMenuItem = () => {
    hoverTimerRef.current = setTimeout(() => {
      setHoveredMenu(null);
    }, 200);
  };

  const handleMouseEnterFlyout = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
  };

  const handleMouseLeaveFlyout = () => {
    hoverTimerRef.current = setTimeout(() => {
      setHoveredMenu(null);
    }, 200);
  };

  // Outside click handlers
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (shopSwitcherRef.current && !shopSwitcherRef.current.contains(event.target)) setShowShopSwitcher(false);
      if (themePickerRef.current && !themePickerRef.current.contains(event.target)) setShowThemePicker(false);
      if (desktopSearchRef.current && !desktopSearchRef.current.contains(event.target)) setDesktopSearchQuery('');
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch Shops
  const loadUserShops = async () => {
    if (!user?.id) return;
    setLoadingShops(true);
    try {
      const res = await fetch('https://ourzestapp.online/oz_backend/root_users.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'get_user_shop_roles', user_id: user.id })
      });
      const data = await res.json();
      if (data.success && data.data) {
        setUserShops(data.data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingShops(false);
    }
  };

  const handleShopSwitch = (shop) => {
    const updatedUser = {
      ...user,
      shop_id: shop.shop_id,
      shop_name: shop.shop_name,
      shop_code: shop.shop_code,
      role: shop.role || user.role
    };
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
    setShowShopSwitcher(false);
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="modern-loader-screen">
        <div className="modern-spinner-square"></div>
        <p>{translations.loading || 'Loading workspace...'}</p>
      </div>
    );
  }

  const desktopSearchResults = getSearchResults(desktopSearchQuery);

  return (
    <div className={`oz-dashboard-shell ${darkMode ? 'dark-mode' : 'light-mode'} ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      
      {/* ==============================================
          LEFT SIDEBAR
         ============================================== */}
      <aside className="oz-sidebar">
        
        {/* LOGO & DYNAMIC BUSINESS NAME */}
        <div className="oz-sidebar-header">
          <div className="oz-brand-identity">
            <div className="oz-brand-logo-wrapper">
              {businessData.bussinesslogo ? (
                <img src={businessData.bussinesslogo} alt="Business Logo" className="oz-brand-logo" />
              ) : (
                <div className="oz-brand-logo-fallback">
                  <FontAwesomeIcon icon={Icons.faStore} />
                </div>
              )}
            </div>
            {!sidebarCollapsed && (
              <div className="oz-brand-name-box">
                <h1 className="oz-brand-name" title={businessData.businessname}>
                  {businessData.businessname}
                </h1>
              </div>
            )}
          </div>
        </div>

        {/* SIDEBAR NAVIGATION SECTIONS & MENUS */}
        <div className="oz-sidebar-nav-scroll">
          <nav className="oz-sidebar-nav">
            {groupedMenuSections.map((section) => {
              const isSectionCollapsed = collapsedSectionIds.includes(section.id);
              const showSectionHeader = section.name && section.name !== 'Main Navigation';

              return (
                <div key={section.id} className="oz-sidebar-section">
                  {showSectionHeader && !sidebarCollapsed && (
                    <div 
                      className="oz-section-header"
                      onClick={() => toggleSectionCollapse(section.id)}
                      title="Toggle Section"
                    >
                      <span className="oz-section-title">{section.name}</span>
                      <FontAwesomeIcon 
                        icon={isSectionCollapsed ? Icons.faChevronRight : Icons.faChevronDown} 
                        className="oz-section-chevron" 
                      />
                    </div>
                  )}

                  {(!isSectionCollapsed || sidebarCollapsed) && (
                    <div className="oz-section-menu-list">
                      {section.menus.map((menu) => {
                        const isActive = activeMenu === menu.id;
                        const isExpanded = expandedMenuIds.includes(menu.id);
                        const hasSubmenus = menu.submenus && menu.submenus.length > 0;

                        return (
                          <div 
                            key={menu.id} 
                            className={`oz-menu-item-group ${isActive ? 'active' : ''}`}
                            onMouseEnter={(e) => handleMouseEnterMenuItem(e, menu)}
                            onMouseLeave={handleMouseLeaveMenuItem}
                          >
                            <div 
                              className={`oz-menu-item ${isActive ? 'is-active' : ''}`} 
                              onClick={() => handleMenuClick(menu.id)}
                            >
                              <span className="oz-menu-icon">
                                <FontAwesomeIcon icon={getIcon(menu.icon)} />
                              </span>
                              {!sidebarCollapsed && (
                                <span className="oz-menu-label">{menu.translatedName}</span>
                              )}
                              {!sidebarCollapsed && hasSubmenus && (
                                <FontAwesomeIcon 
                                  icon={isExpanded ? Icons.faChevronUp : Icons.faChevronDown} 
                                  className="oz-submenu-arrow" 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleMenuExpansion(menu.id);
                                  }}
                                />
                              )}
                            </div>

                            {!sidebarCollapsed && isExpanded && hasSubmenus && (
                              <div className="oz-submenu-list">
                                {menu.submenus.map((sub) => (
                                  <div
                                    key={sub.id}
                                    className={`oz-submenu-item ${activeSubmenu === sub.id ? 'is-sub-active' : ''}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSubmenuClick(menu.id, sub.id);
                                    }}
                                  >
                                    <span className="oz-submenu-bullet"></span>
                                    <span className="oz-submenu-label">{sub.translatedName}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>

        {/* FOOTER: LOGOUT BUTTON */}
        <div className="oz-sidebar-footer">
          <button className="oz-sidebar-logout-btn" onClick={logout} title={translations.logout || 'Logout'}>
            <FontAwesomeIcon icon={Icons.faSignOutAlt} />
            {!sidebarCollapsed && <span>{translations.logout || 'Logout'}</span>}
          </button>
        </div>
      </aside>

      {/* COLLAPSED FLYOUT POPOVER */}
      {sidebarCollapsed && hoveredMenu && hoveredMenu.submenus && hoveredMenu.submenus.length > 0 && (
        <div 
          className="oz-sidebar-flyout" 
          style={{ top: `${flyoutPosition.top}px` }}
          onMouseEnter={handleMouseEnterFlyout}
          onMouseLeave={handleMouseLeaveFlyout}
        >
          <div className="oz-flyout-header">{hoveredMenu.translatedName}</div>
          <div className="oz-flyout-body">
            {hoveredMenu.submenus.map((sub) => (
              <div 
                key={sub.id} 
                className={`oz-flyout-item ${activeSubmenu === sub.id ? 'active' : ''}`}
                onClick={() => handleSubmenuClick(hoveredMenu.id, sub.id)}
              >
                <FontAwesomeIcon icon={getIcon(sub.icon)} className="oz-flyout-icon" />
                <span>{sub.translatedName}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ==============================================
          MAIN VIEWPORT & REDESIGNED TOP NAVBAR
         ============================================== */}
      <div className="oz-main-viewport">
        
        {/* TOP NAVBAR */}
        <header className="oz-top-navbar">
          <div className="oz-nav-left">
            
            {/* 3-LINE HAMBURGER TOGGLE BUTTON LEFT OF BREADCRUMB */}
            <button 
              className="oz-sidebar-toggle-btn" 
              onClick={() => {
                if (window.innerWidth <= 768) {
                  setMobileSheetOpen(true);
                } else {
                  setSidebarCollapsed(!sidebarCollapsed);
                }
              }}
              title="Toggle Sidebar"
            >
              <FontAwesomeIcon icon={Icons.faBars} />
            </button>

            {/* DYNAMIC BREADCRUMB TRAIL IN GLASS BADGE */}
            <div className="oz-breadcrumb-badge-wrapper">
              <nav className="oz-breadcrumb-trail" aria-label="breadcrumb">
                {breadcrumbItems.map((crumb, idx) => (
                  <React.Fragment key={crumb.id || idx}>
                    {idx > 0 && <FontAwesomeIcon icon={Icons.faChevronRight} className="oz-crumb-separator" />}
                    <span 
                      className={`oz-crumb-link ${crumb.isClickable ? 'clickable' : 'active'}`}
                      onClick={() => crumb.isClickable && handleMenuClick(crumb.id)}
                    >
                      {crumb.label}
                    </span>
                  </React.Fragment>
                ))}
              </nav>
            </div>

            {/* DESKTOP TOP NAV SEARCH BAR FOR MENUS & SUBMENUS */}
            <div className="oz-desktop-search-container" ref={desktopSearchRef}>
              <div className="oz-search-input-box">
                <FontAwesomeIcon icon={Icons.faSearch} className="oz-search-icon" />
                <input 
                  type="text" 
                  placeholder={translations.searchPlaceholder || "Search menus, submenus..."} 
                  value={desktopSearchQuery}
                  onChange={(e) => setDesktopSearchQuery(e.target.value)}
                  className="oz-desktop-search-input"
                />
                {desktopSearchQuery && (
                  <button className="oz-search-clear-btn" onClick={() => setDesktopSearchQuery('')}>
                    <FontAwesomeIcon icon={Icons.faTimes} />
                  </button>
                )}
              </div>

              {/* Instant Search Results Dropdown */}
              {desktopSearchQuery.trim() && (
                <div className="oz-dropdown-menu oz-search-results-menu">
                  <div className="oz-dropdown-header">SEARCH RESULTS</div>
                  {desktopSearchResults.length === 0 ? (
                    <div className="oz-no-search-results">No matching menus found</div>
                  ) : (
                    desktopSearchResults.map((res, i) => (
                      <div 
                        key={i} 
                        className="oz-dropdown-option oz-search-result-item"
                        onClick={() => {
                          if (res.submenuId) {
                            handleSubmenuClick(res.menuId, res.submenuId);
                          } else {
                            handleMenuClick(res.menuId);
                          }
                          setDesktopSearchQuery('');
                        }}
                      >
                        <div className="oz-search-res-info">
                          <FontAwesomeIcon icon={getIcon(res.icon)} className="oz-search-res-icon" />
                          <div className="oz-search-res-titles">
                            <span className="oz-search-res-main">{res.title}</span>
                            <small className="oz-search-res-parent">{res.parent}</small>
                          </div>
                        </div>
                        <FontAwesomeIcon icon={Icons.faChevronRight} className="oz-result-arrow" />
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

          </div>

          {/* RIGHT ACTION CONTROLS */}
          <div className="oz-nav-right">
            
            {/* DARK/LIGHT MODE TOGGLE WITH LABEL & ICON (DESKTOP) / ICON-ONLY (MOBILE) */}
            <button 
              className="oz-nav-pill-btn oz-mode-toggle-btn" 
              onClick={toggleDarkModeWithRipple} 
              title="Toggle Dark/Light Mode"
            >
              <FontAwesomeIcon icon={darkMode ? Icons.faSun : Icons.faMoon} />
              <span className="oz-desktop-pill-label">{darkMode ? "Light" : "Dark"}</span>
            </button>

            {/* THEME COLOR PICKER BUTTON WITH LABEL & DROPDOWN (16 PRESETS) */}
            <div className="oz-dropdown-container" ref={themePickerRef}>
              <button 
                className="oz-nav-pill-btn oz-theme-btn" 
                onClick={() => setShowThemePicker(!showThemePicker)}
                title="Change Theme Color"
              >
                <FontAwesomeIcon icon={Icons.faPalette} style={{ color: currentTheme }} />
                <span className="oz-desktop-pill-label">Theme</span>
                <span className="oz-theme-indicator-dot" style={{ backgroundColor: currentTheme }}></span>
              </button>

              {showThemePicker && (
                <div className="oz-dropdown-menu oz-theme-menu">
                  <div className="oz-dropdown-header">{translations.themeColor || 'THEME COLOR'}</div>
                  <div className="oz-theme-options-grid">
                    {THEME_PRESETS.map((t) => (
                      <button
                        key={t.hex}
                        className={`oz-theme-color-swatch ${currentTheme === t.hex ? 'active' : ''}`}
                        style={{ backgroundColor: t.hex }}
                        onClick={() => { applyTheme(t.hex); setShowThemePicker(false); }}
                        title={t.name}
                      >
                        {currentTheme === t.hex && <FontAwesomeIcon icon={Icons.faCheck} />}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* SHOP SWITCHER BUTTON (DESKTOP ONLY - COMPLETELY REMOVED ON MOBILE) */}
            {currentShop && (
              <div className="oz-dropdown-container oz-desktop-shop-container" ref={shopSwitcherRef}>
                <button className="oz-shop-switch-pill" onClick={() => { setShowShopSwitcher(!showShopSwitcher); loadUserShops(); }}>
                  <FontAwesomeIcon icon={Icons.faStore} className="oz-shop-pill-icon" />
                  <span className="oz-shop-pill-name">{currentShop.shop_name || 'SELECT SHOP'}</span>
                  <FontAwesomeIcon icon={Icons.faChevronDown} className="oz-pill-arrow" />
                </button>

                {showShopSwitcher && (
                  <div className="oz-dropdown-menu oz-shop-menu">
                    <div className="oz-dropdown-header">{translations.switchShops || 'SWITCH SHOP'}</div>
                    {loadingShops ? (
                      <div className="p-3 text-center text-muted">Loading shops...</div>
                    ) : (
                      userShops.map(shop => (
                        <div 
                          key={shop.shop_id} 
                          className={`oz-dropdown-option ${shop.shop_id === currentShop.shop_id ? 'selected' : ''}`}
                          onClick={() => handleShopSwitch(shop)}
                        >
                          <div className="d-flex flex-column">
                            <span className="fw-bold">{shop.shop_name}</span>
                          
                          </div>
                          {shop.shop_id === currentShop.shop_id && <FontAwesomeIcon icon={Icons.faCheck} />}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}

            {/* NON-CLICKABLE ROUND PROFILE AVATAR DISPLAY WITH STATUS DOT */}
            <div className="oz-user-profile-display" title={businessData.fullname}>
              {businessData.profile_image ? (
                <img src={businessData.profile_image} alt="User Profile" className="oz-user-avatar-img" />
              ) : (
                <div className="oz-user-avatar-initials">
                  {getInitials(businessData.fullname)}
                </div>
              )}
              <span className="oz-online-status-dot" title="Online"></span>
            </div>

          </div>
        </header>

        {/* MAIN CONTENT AREA */}
        <main className="oz-content-area">
          <Suspense fallback={<div className="p-4 text-center">Loading module...</div>}>
            {formContent}
          </Suspense>
        </main>
      </div>

      {/* MOBILE NATIVE BOTTOM-TO-TOP SHEET */}
      {mobileSheetOpen && (
        <div className="oz-mobile-sheet-overlay" onClick={() => { setMobileSheetOpen(false); setMobileFocusedMenu(null); setMobileSearchQuery(''); }}>
          <div className="oz-mobile-bottom-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="oz-mobile-sheet-handle"></div>
            
            <div className="oz-mobile-sheet-header">
              <h3>Navigation Menu</h3>
              <button className="oz-mobile-sheet-close" onClick={() => { setMobileSheetOpen(false); setMobileFocusedMenu(null); setMobileSearchQuery(''); }}>
                <FontAwesomeIcon icon={Icons.faTimes} />
              </button>
            </div>

            <div className="oz-mobile-sheet-body">
              
              {/* MOBILE MENU SEARCH BAR */}
              <div className="oz-mobile-search-box">
                <FontAwesomeIcon icon={Icons.faSearch} className="oz-mobile-search-icon" />
                <input 
                  type="text" 
                  placeholder="Search menus & submenus..." 
                  value={mobileSearchQuery}
                  onChange={(e) => setMobileSearchQuery(e.target.value)}
                  className="oz-mobile-search-input"
                />
                {mobileSearchQuery && (
                  <button className="oz-mobile-search-clear" onClick={() => setMobileSearchQuery('')}>
                    <FontAwesomeIcon icon={Icons.faTimes} />
                  </button>
                )}
              </div>

              {/* SEARCH RESULTS IF SEARCH QUERY IS TYPED */}
              {mobileSearchQuery.trim() ? (
                <div className="oz-mobile-search-results-list">
                  {getSearchResults(mobileSearchQuery).length === 0 ? (
                    <div className="p-3 text-center text-muted">No matching menus found</div>
                  ) : (
                    getSearchResults(mobileSearchQuery).map((res, i) => (
                      <div 
                        key={i} 
                        className="oz-mobile-search-result-card"
                        onClick={() => {
                          if (res.submenuId) {
                            handleSubmenuClick(res.menuId, res.submenuId);
                          } else {
                            handleMenuClick(res.menuId);
                          }
                          setMobileSearchQuery('');
                        }}
                      >
                        <FontAwesomeIcon icon={getIcon(res.icon)} className="oz-mobile-res-icon" />
                        <div className="oz-mobile-res-text">
                          <span className="oz-mobile-res-title">{res.title}</span>
                          <small className="oz-mobile-res-parent">{res.parent}</small>
                        </div>
                        <FontAwesomeIcon icon={Icons.faChevronRight} className="oz-mobile-res-arrow" />
                      </div>
                    ))
                  )}
                </div>
              ) : (
                /* REGULAR MENU SECTIONS WITH FOCUS BLUR */
                groupedMenuSections.map((section) => (
                  <div key={section.id} className="oz-mobile-section-block">
                    {section.name && section.name !== 'Main Navigation' && (
                      <div className="oz-mobile-section-title">{section.name}</div>
                    )}

                    {section.menus.map((menu) => {
                      const isFocused = mobileFocusedMenu === menu.id;
                      const isOtherBlurred = mobileFocusedMenu !== null && !isFocused;
                      const hasSubmenus = menu.submenus && menu.submenus.length > 0;

                      return (
                        <div 
                          key={menu.id} 
                          className={`oz-mobile-menu-card ${isFocused ? 'focused' : ''} ${isOtherBlurred ? 'blurred' : ''}`}
                          onClick={() => {
                            if (hasSubmenus) {
                              setMobileFocusedMenu(isFocused ? null : menu.id);
                            } else {
                              handleMenuClick(menu.id);
                            }
                          }}
                        >
                          <div className="oz-mobile-menu-row">
                            <span className="oz-mobile-menu-icon">
                              <FontAwesomeIcon icon={getIcon(menu.icon)} />
                            </span>
                            <span className="oz-mobile-menu-title">{menu.translatedName}</span>
                            {hasSubmenus && (
                              <FontAwesomeIcon 
                                icon={isFocused ? Icons.faChevronUp : Icons.faChevronDown} 
                                className="oz-mobile-arrow" 
                              />
                            )}
                          </div>

                          {hasSubmenus && isFocused && (
                            <div className="oz-mobile-submenus-container">
                              {menu.submenus.map((sub) => (
                                <div 
                                  key={sub.id} 
                                  className="oz-mobile-submenu-item"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSubmenuClick(menu.id, sub.id);
                                  }}
                                >
                                  <FontAwesomeIcon icon={getIcon(sub.icon)} />
                                  <span>{sub.translatedName}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* ==============================================
          MOBILE FLOATING PILL BOTTOM NAVIGATION DOCK
         ============================================== */}
      <nav className="oz-mobile-bottom-dock">
        
        {/* 1. Dashboard */}
        <button 
          className={`oz-dock-item ${activeMenu === 'dashboard' && !mobileSheetOpen ? 'active' : ''}`}
          onClick={() => { handleMenuClick('dashboard'); setMobileSheetOpen(false); }}
        >
          <FontAwesomeIcon icon={Icons.faHome} className="oz-dock-icon" />
          <span className="oz-dock-text">Dashboard</span>
        </button>

        {/* 2. Stock */}
        <button 
          className={`oz-dock-item ${activeMenu === stockMenuItem?.id && !mobileSheetOpen ? 'active' : ''}`}
          onClick={() => {
            if (stockMenuItem) {
              handleMenuClick(stockMenuItem.id);
            } else {
              handleMenuClick('stock');
            }
            setMobileSheetOpen(false);
          }}
        >
          <FontAwesomeIcon icon={stockMenuItem ? getIcon(stockMenuItem.icon) : Icons.faBoxes} className="oz-dock-icon" />
          <span className="oz-dock-text">Stock</span>
        </button>

        {/* 3. Sale */}
        <button 
          className={`oz-dock-item ${activeMenu === saleMenuItem?.id && !mobileSheetOpen ? 'active' : ''}`}
          onClick={() => {
            if (saleMenuItem) {
              handleMenuClick(saleMenuItem.id);
            } else {
              handleMenuClick('sales');
            }
            setMobileSheetOpen(false);
          }}
        >
          <FontAwesomeIcon icon={saleMenuItem ? getIcon(saleMenuItem.icon) : Icons.faShoppingCart} className="oz-dock-icon" />
          <span className="oz-dock-text">Sale</span>
        </button>

        {/* 4. Purchase */}
        <button 
          className={`oz-dock-item ${activeMenu === purchaseMenuItem?.id && !mobileSheetOpen ? 'active' : ''}`}
          onClick={() => {
            if (purchaseMenuItem) {
              handleMenuClick(purchaseMenuItem.id);
            } else {
              handleMenuClick('purchases');
            }
            setMobileSheetOpen(false);
          }}
        >
          <FontAwesomeIcon icon={purchaseMenuItem ? getIcon(purchaseMenuItem.icon) : Icons.faTruck} className="oz-dock-icon" />
          <span className="oz-dock-text">Purchase</span>
        </button>

        {/* 5. Menu */}
        <button 
          className={`oz-dock-item ${mobileSheetOpen ? 'active' : ''}`}
          onClick={() => setMobileSheetOpen(!mobileSheetOpen)}
        >
          <FontAwesomeIcon icon={Icons.faEllipsisH} className="oz-dock-icon" />
          <span className="oz-dock-text">Menu</span>
        </button>

        {/* iOS style Home Indicator bar inside dock */}
        <div className="oz-dock-home-indicator"></div>
      </nav>

    </div>
  );
};

export default DashboardBusinessManager;